/**
 * serverClock — ONE source of truth for "what time is it on the server?"
 *
 * THE PROBLEM
 * -----------
 * Every Fun Challenges timer compares the server's absolute `startsAt`/`endsAt`
 * epochs against the CLIENT's `Date.now()`. Epoch ms are absolute UTC, so a
 * wrong TIMEZONE is harmless — but a wrong WALL CLOCK is not. A user whose
 * machine runs 5 minutes fast computes `endsAt - Date.now()` as a negative
 * number and sees 0:00 with no time to play; one running slow gets extra time
 * on screen and then a race that ends "early" from their point of view. The
 * server always enforces the true window off its own clock, so the damage is
 * to what the player SEES and to the client-side move gate.
 *
 * This has bitten the product three times (handover Rounds 54, 56 and 57). Each
 * was patched locally: the royale timer pill anchors to the observed 3-2-1
 * finish (`raceStartLocalRef`), and the countdown anchors a fixed duration
 * (`countdownAnchorRef`). Both work by measuring a DELTA of the client's own
 * clock, so constant skew cancels — but they only exist on the fresh-start
 * path. A cold mid-race REJOIN never sees a countdown, has no anchor, and fell
 * straight back to raw `endsAt - Date.now()`. The 1v1 battle screen never got
 * any of the three fixes at all.
 *
 * THE FIX
 * -------
 * The server now stamps `serverNow` (its own `Date.now()`) on every envelope
 * that carries a timestamp: `fun_session_start`, `fun_session_rejoin` and the
 * spectate bootstrap. On receipt we compute
 *
 *     offset = clientNow - serverNow      // + when the device runs FAST
 *
 * and thereafter read `serverNowMs()` (= `Date.now() - offset`) instead of
 * `Date.now()` wherever an absolute server timestamp is involved. Network
 * latency makes the estimate late by roughly the one-way trip (tens of ms) —
 * irrelevant against a 90 s race and against the multi-minute skews this
 * exists to defend from.
 *
 * WHY A MODULE AND NOT STORE STATE
 * --------------------------------
 * Two independent stores (royale + 1v1 battle) and several components need it,
 * and the value is a property of the DEVICE, not of any one session — a single
 * module keeps them from drifting apart the way the three earlier local fixes
 * did. It is deliberately NOT reset between sessions: an offset measured during
 * the last race is still the best estimate for the next one.
 *
 * BACKWARD COMPATIBILITY
 * ----------------------
 * `serverNow` is additive. Against an OLD server that doesn't send it, the
 * offset simply stays at its previous value (0 on a cold start) and every call
 * degrades to exactly today's behaviour — so the web client is safe to deploy
 * before, after, or without the game-service.
 */

/** clientClock − serverClock, in ms. Positive ⇒ this device runs FAST. */
let clockOffsetMs = 0;
/** Whether we've ever had a real measurement (vs. the 0 default). */
let measured = false;

/**
 * Record a server timestamp observed on an inbound envelope.
 *
 * Accepts the raw wire value (the server sends it as a STRING to dodge any
 * JSON large-number handling); non-numeric / missing input is ignored, which is
 * exactly the old-server case.
 */
export function noteServerNow(serverNow: unknown): void {
  const server =
    typeof serverNow === 'number' ? serverNow : parseInt(String(serverNow), 10);
  if (!Number.isFinite(server) || server <= 0) {
    return;
  }
  clockOffsetMs = Date.now() - server;
  measured = true;
}

/**
 * "Now" on the SERVER's clock. Use this for every comparison against an
 * absolute server timestamp (`startsAt`, `endsAt`) in place of `Date.now()`.
 */
export function serverNowMs(): number {
  return Date.now() - clockOffsetMs;
}

/**
 * Remaining ms until an absolute server timestamp, floored at 0.
 * `endsAt` may arrive as a string on the wire.
 */
export function msUntil(target: number | string | undefined | null): number {
  const t = typeof target === 'number' ? target : parseInt(String(target), 10);
  if (!Number.isFinite(t) || t <= 0) {
    return 0;
  }
  return Math.max(0, t - serverNowMs());
}

/** Current estimate of (clientClock − serverClock) in ms. Debug/telemetry. */
export function getClockOffsetMs(): number {
  return clockOffsetMs;
}

/** True once a real `serverNow` has been observed. Debug/telemetry. */
export function hasMeasuredClock(): boolean {
  return measured;
}

/** TEST-ONLY: restore the pristine unmeasured state. */
export function __resetServerClockForTests(): void {
  clockOffsetMs = 0;
  measured = false;
}
